/*
 * The contents of this file were largely copied from
 * src/frontend/react-lib/src/components/FrequentlyAskedQuestions/FrequentlyAskedQuestions.tsx
 * and modified to match the designs of training FAQs.
 */

import React, { createContext, useContext } from 'react';
import styled from 'styled-components';

import { Box, Display, Paragraph, Text } from '@rover/kibble/core';
import type { Props as BoxProps } from '@rover/kibble/core/Box';
import type { Props as DisplayProps } from '@rover/kibble/core/Display';
import type { Props as ParagraphProps } from '@rover/kibble/core/Paragraph';
import { MarginSystemProps } from '@rover/kibble/core/styledSystemPropTypes';
import Accordion, {
  AccordionBodyContent,
  AccordionHeaderClickTarget,
  AccordionHeaderIconWrapper,
} from '@rover/react-lib/src/components/layouts/Accordion';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { TrainingFaqSection } from '../../events/analyticsEvents';

const FaqMicroDataContext = createContext<boolean>(true);

export type Props = Omit<BoxProps, 'tx' | 'variant'> &
  MarginSystemProps & {
    htmlRef?: React.RefObject<HTMLElement | null>;
    /**
     * Whether elements should emit schema.org microdata attributes.
     * Set to false when a page renders duplicate FAQ content to avoid
     * emitting two FAQPage scopes on the same URL — see
     * https://developers.google.com/search/docs/appearance/structured-data/faqpage
     */
    includeMicroData?: boolean;
  };

type SubComponents = {
  Header: typeof Display;
  Subheader: typeof Paragraph;
};

const ICON_WRAPPER_SIZE = 32;

const StyledAccordion = styled(Accordion)`
  &:first-of-type {
    border-top: 0 none;
  }

  ${AccordionBodyContent} {
    padding-top: 0px;
    padding-right: 16px;
  }

  ${AccordionHeaderClickTarget} {
    // this element doesn't specify left- and right- padding, leaving the browser's default padding for <button>. In order to get a standard width on the icon wrapper, we need to remove this padding deliberately.
    padding-left: 0;
    padding-right: 16px;
    gap: 4px;
  }

  ${AccordionHeaderIconWrapper} {
    // TODO - This is a bandaid fix to align the icon horizontally with the question text and vertically with the answer text. We should uplift Accordion eventually and fix this properly.
    flex-basis: ${ICON_WRAPPER_SIZE}px;
    align-self: flex-start;
    padding: 0;
    text-align: center;
    line-height: ${ICON_WRAPPER_SIZE}px;
  }
`;

const FrequentlyAskedQuestions: React.FC<Props> & SubComponents = ({
  px = ['4x', '4x'],
  py = '12x',
  ml = ['0x', '2x'],
  children,
  htmlRef,
  includeMicroData = true,
  ...boxProps
}: Props) => (
  <FaqMicroDataContext.Provider value={includeMicroData}>
    <Box
      webProps={
        includeMicroData ? { itemScope: true, itemType: 'https://schema.org/FAQPage' } : undefined
      }
      py={py}
      px={px}
      ml={ml}
      {...boxProps}
      ref={htmlRef}
    >
      <Box flex="2 1 80%">{children}</Box>
    </Box>
  </FaqMicroDataContext.Provider>
);

FrequentlyAskedQuestions.Header = ({ children, ...displayProps }: DisplayProps) => (
  <Display size="400" mb="4x" {...displayProps}>
    {children}
  </Display>
);

FrequentlyAskedQuestions.Subheader = ({ children, ...paragraphProps }: ParagraphProps) => (
  <Paragraph webProps={{ maxWidth: 'none' }} size="200" mb="6x" {...paragraphProps}>
    {children}
  </Paragraph>
);

const Question: React.FC<{ children: string }> = ({ children }) => {
  const includeMicroData = useContext(FaqMicroDataContext);
  return (
    <Text
      fontWeight="semibold"
      size="200"
      textColor="primary"
      itemProp={includeMicroData ? 'name' : undefined}
    >
      {children}
    </Text>
  );
};

const Answer: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const includeMicroData = useContext(FaqMicroDataContext);
  return (
    <Box
      webProps={
        includeMicroData
          ? {
              itemScope: true,
              itemProp: 'acceptedAnswer',
              itemType: 'https://schema.org/Answer',
            }
          : undefined
      }
    >
      <Box webProps={includeMicroData ? { itemProp: 'text' } : undefined}>{children}</Box>
    </Box>
  );
};

export type FrequentlyAskedQuestionProps = {
  question: string;
  children: React.ReactElement;
} & Omit<
  React.ComponentProps<typeof Accordion>,
  'header' | 'body' | 'isStackedAccordion' | 'getIcon' | 'iconLeft'
>;

export const FrequentlyAskedQuestion: React.FC<FrequentlyAskedQuestionProps> = ({
  question,
  children: answer,
  onToggle,
  ...props
}) => {
  const includeMicroData = useContext(FaqMicroDataContext);

  const onFaqOpen = (isOpen: boolean): void => {
    emitAnalyticsEvent(new TrainingFaqSection({ question, isOpen }));
    onToggle?.(isOpen);
  };

  const microDataProps = includeMicroData
    ? {
        itemScope: true,
        itemProp: 'mainEntity',
        itemType: 'https://schema.org/Question',
      }
    : {};

  return (
    <StyledAccordion
      header={<Question>{question}</Question>}
      body={<Answer>{answer}</Answer>}
      isStackedAccordion={false}
      getIcon={(isOpen) => (isOpen ? 'rover-icon-in-up' : 'rover-icon-in-down')}
      onToggle={onFaqOpen}
      isRightIcon
      {...props}
      {...microDataProps}
    />
  );
};

export default FrequentlyAskedQuestions;
